import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { generateGroupInfoQueryOptions } from '@/hooks/groups/use-group-info';
import type { RespondedType, ResponseError } from '@/models/response';
import { useConversationSessionStore } from '@/stores/conversation-session';
import { addToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';
import { generateSessionKey } from '@/utils/session';

export default function useUpdateGroupInfo(groupId: Nullable<number>) {
  const queryClient = useQueryClient();

  return useMutation<RespondedType, ResponseError, string | null>({
    mutationKey: ['/UpdateGroupInfo', groupId],
    mutationFn: (params) =>
      fetcher('/UpdateGroupInfo', { Info: params, GroupID: groupId }, { validResponseCodes: [0] }),
    onSuccess: (_, params) => {
      if (groupId) {
        queryClient.setQueryData(generateGroupInfoQueryOptions(groupId).queryKey, (state) =>
          produce(state, (draft) => {
            if (draft?.GroupData) {
              draft.GroupData.Info = params;
            }
          }),
        );
        useConversationSessionStore.getState().setLastReadNotice(generateSessionKey('group', groupId), params);
      }
    },
    onSettled: (data, error) =>
      addToast({
        type: data ? 'success' : 'error',
        message: data?.Msg ?? error?.message,
      }),
  });
}
